import { useCallback, useEffect, useMemo, useState } from 'react';

import type { CompoundSeries } from '../components/charts/CompoundLineChart';
import { colors } from '../core/theme/tokens';
import { useAssumedReturn } from '../contexts/AssumedReturnContext';
import type { SampleAsset } from '../types/financial';
import { longTermReturnPct } from '../utils/householdFromDraft';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  DEFAULT_WEALTH_TARGET,
  INTEREST_COLOR,
  MAX_ANNUAL_RATE_PCT,
  MAX_WEALTH_TARGET,
  MIN_ANNUAL_RATE_PCT,
  MIN_WEALTH_TARGET,
  PRINCIPAL_COLOR,
  buildWealthMilestones,
  clampWealthTarget,
  projectCompounding,
  rowsToInterestPoints,
  rowsToPrincipalPoints,
  rowsToSeriesPoints,
  splitFromRows,
  weightedPortfolioRatePct,
  type CompoundYearRow,
  type PrincipalInterestSplit,
  type WealthMilestonePlan,
} from '../utils/investmentCompounding';

const DEFAULT_YEARS = 20;

export type InvestmentHoldingRow = {
  id: string;
  label: string;
  value: number;
  expectedReturnPct: number;
  sharePct: number;
};

export type InvestmentCompounding = {
  holdings: InvestmentHoldingRow[];
  /** Editable starting investment (seeded from portfolio). */
  principal: number;
  /** Editable annual contribution (seeded from account plans). */
  annualContribution: number;
  /** Editable assumed annual return (%). */
  annualRatePct: number;
  /** Portfolio-derived seeds before user edits. */
  seedPrincipal: number;
  seedAnnualContribution: number;
  seedExpectedRatePct: number;
  years: number;
  startYear: number;
  targetAmount: number;
  tableRows: CompoundYearRow[];
  chartSeries: CompoundSeries[];
  categorySplits: PrincipalInterestSplit[];
  milestonePlan: WealthMilestonePlan;
  /** First year where growth exceeds that year's contribution. */
  compoundingKickInYear: number | null;
  setYears: (years: number) => void;
  setPrincipal: (value: number) => void;
  nudgePrincipal: (delta: number) => void;
  setAnnualContribution: (value: number) => void;
  nudgeAnnualContribution: (delta: number) => void;
  setAnnualRatePct: (value: number) => void;
  nudgeAnnualRatePct: (delta: number) => void;
  setTargetAmount: (value: number) => void;
  nudgeTargetAmount: (delta: number) => void;
  resetToPortfolio: () => void;
  resetAssumptions: () => void;
};

function findKickInYear(rows: CompoundYearRow[]): number | null {
  const hit = rows.find((r) => r.yearIndex > 0 && r.growth > r.contributions && r.contributions > 0);
  return hit?.year ?? null;
}

export function useInvestmentCompounding(
  assets: SampleAsset[],
  options?: { startYear?: number; monthlyContribution?: number },
): InvestmentCompounding {
  const investments = useMemo(
    () => assets.filter((a) => a.isInvestment && a.value > 0),
    [assets],
  );

  const seedPrincipal = useMemo(
    () => investments.reduce((sum, a) => sum + a.value, 0),
    [investments],
  );

  const seedAnnualContribution = useMemo(() => {
    if (options?.monthlyContribution != null) {
      return Math.max(0, options.monthlyContribution) * 12;
    }
    return investments.reduce((sum, a) => sum + Math.max(0, a.monthlyContribution ?? 0), 0) * 12;
  }, [options?.monthlyContribution, investments]);

  const seedExpectedRatePct = useMemo(
    () =>
      weightedPortfolioRatePct(
        investments.map((a) => ({
          value: a.value,
          expectedReturnPct: longTermReturnPct(a.expectedReturnPct),
        })),
      ),
    [investments],
  );

  const holdings = useMemo<InvestmentHoldingRow[]>(() => {
    const total = seedPrincipal || 1;
    return investments.map((a) => ({
      id: a.id,
      label: a.label,
      value: a.value,
      expectedReturnPct: a.expectedReturnPct ?? DEFAULT_ANNUAL_RATE_PCT,
      sharePct: Math.round((a.value / total) * 100),
    }));
  }, [investments, seedPrincipal]);

  const startYear = options?.startYear ?? new Date().getFullYear();
  const [years, setYears] = useState(DEFAULT_YEARS);
  const [principal, setPrincipalState] = useState(seedPrincipal);
  const [annualContribution, setAnnualContributionState] = useState(seedAnnualContribution);
  const { annualRatePct, setAnnualRatePct, nudgeAnnualRatePct, resetToDefault } =
    useAssumedReturn();
  const [targetAmount, setTargetAmountState] = useState(DEFAULT_WEALTH_TARGET);
  const [inputsDirty, setInputsDirty] = useState(false);

  useEffect(() => {
    if (!inputsDirty) {
      setPrincipalState(seedPrincipal);
      setAnnualContributionState(seedAnnualContribution);
    }
  }, [seedPrincipal, seedAnnualContribution, inputsDirty]);

  const setPrincipal = useCallback((value: number) => {
    setInputsDirty(true);
    setPrincipalState(Math.max(0, Math.round(value)));
  }, []);

  const nudgePrincipal = useCallback((delta: number) => {
    setInputsDirty(true);
    setPrincipalState((prev) => Math.max(0, Math.round(prev + delta)));
  }, []);

  const setAnnualContribution = useCallback((value: number) => {
    setInputsDirty(true);
    setAnnualContributionState(Math.max(0, Math.round(value)));
  }, []);

  const nudgeAnnualContribution = useCallback((delta: number) => {
    setInputsDirty(true);
    setAnnualContributionState((prev) => Math.max(0, Math.round(prev + delta)));
  }, []);

  const setTargetAmount = useCallback((value: number) => {
    setTargetAmountState(clampWealthTarget(value));
  }, []);

  const nudgeTargetAmount = useCallback((delta: number) => {
    setTargetAmountState((prev) => clampWealthTarget(prev + delta));
  }, []);

  const tableRows = useMemo(
    () =>
      projectCompounding({
        principal,
        annualRatePct,
        annualContribution,
        years,
        startYear,
      }),
    [principal, annualRatePct, annualContribution, years, startYear],
  );

  const compoundingKickInYear = useMemo(() => findKickInYear(tableRows), [tableRows]);

  const milestonePlan = useMemo(
    () =>
      buildWealthMilestones({
        principal,
        annualRatePct,
        annualContribution,
        startYear,
        target: targetAmount,
      }),
    [principal, annualRatePct, annualContribution, startYear, targetAmount],
  );

  const chartSeries = useMemo<CompoundSeries[]>(
    () => [
      {
        id: 'principal',
        label: 'Principal (your money)',
        color: PRINCIPAL_COLOR,
        points: rowsToPrincipalPoints(tableRows),
      },
      {
        id: 'interest',
        label: 'Interest (compounded)',
        color: INTEREST_COLOR,
        points: rowsToInterestPoints(tableRows),
      },
      {
        id: 'total',
        label: `Total · ${annualRatePct.toFixed(1)}%`,
        color: colors.text,
        points: rowsToSeriesPoints(tableRows),
        dashed: true,
      },
    ],
    [tableRows, annualRatePct],
  );

  const categorySplits = useMemo(
    () => [
      splitFromRows(
        'path',
        `${annualRatePct.toFixed(1)}% path`,
        annualRatePct,
        colors.accent,
        tableRows,
      ),
    ],
    [tableRows, annualRatePct],
  );

  const resetToPortfolio = useCallback(() => {
    setInputsDirty(false);
    setPrincipalState(seedPrincipal);
    setAnnualContributionState(seedAnnualContribution);
    setAnnualRatePct(seedExpectedRatePct);
  }, [seedPrincipal, seedAnnualContribution, seedExpectedRatePct, setAnnualRatePct]);

  const resetAssumptions = useCallback(() => {
    setInputsDirty(false);
    setYears(DEFAULT_YEARS);
    setTargetAmountState(DEFAULT_WEALTH_TARGET);
    setPrincipalState(seedPrincipal);
    setAnnualContributionState(seedAnnualContribution);
    resetToDefault();
  }, [seedPrincipal, seedAnnualContribution, resetToDefault]);

  return {
    holdings,
    principal,
    annualContribution,
    annualRatePct,
    seedPrincipal,
    seedAnnualContribution,
    seedExpectedRatePct,
    years,
    startYear,
    targetAmount,
    tableRows,
    chartSeries,
    categorySplits,
    milestonePlan,
    compoundingKickInYear,
    setYears,
    setPrincipal,
    nudgePrincipal,
    setAnnualContribution,
    nudgeAnnualContribution,
    setAnnualRatePct,
    nudgeAnnualRatePct,
    setTargetAmount,
    nudgeTargetAmount,
    resetToPortfolio,
    resetAssumptions,
  };
}

export {
  DEFAULT_ANNUAL_RATE_PCT,
  MIN_ANNUAL_RATE_PCT,
  MAX_ANNUAL_RATE_PCT,
  MIN_WEALTH_TARGET,
  MAX_WEALTH_TARGET,
};
